import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthService, CustomerService, trackActionLog } from "../../services";
import { Export } from "../../shared";
import { PencilSquare, Plus } from "react-bootstrap-icons";
import DashboardCustomersList from "../dashboard/DashboardCustomersList";
import BulkEditCustomersWizard from "./BulkEditCustomersWizard";

const CustomersList = () => {
  const navigate = useNavigate();
  const [admissionLogExporting, setAdmissionLogExporting] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditCustomers, setBulkEditCustomers] = useState([]);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const goToCreateNew = () => {
    navigate(`/customers`);
  };

  const additionalButtons = ({
    showExportDropdown,
    onExportToggle,
    exportColumns = [],
    exportData = [],
    selectedCustomers = [],
  }) => {
    const exportAdmissionDischargeLog = async () => {
      if (admissionLogExporting) return;
      setAdmissionLogExporting(true);
      try {
        const response = await CustomerService.exportAdmissionDischargeLog();
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", "Customer_Admission_Discharge_Log.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        trackActionLog({
          actionVerb: "Export",
          summary: "Export Customer Admission & Discharge Log on Customer List page",
          entityType: "export",
          entityId: "Customer_Admission_Discharge_Log",
        }).catch(() => {});
        onExportToggle?.(false);
      } catch (error) {
        console.error("Customer admission discharge log export failed:", error);
        let message = "Failed to export customer admission discharge log.";
        try {
          const data = error?.response?.data;
          if (data instanceof Blob) {
            const text = await data.text();
            try {
              const parsed = JSON.parse(text);
              message = parsed.message || text;
            } catch {
              message = text || message;
            }
          } else if (error?.message) {
            message = error.message;
          }
        } catch {
          /* keep default */
        }
        window.alert(message);
      } finally {
        setAdmissionLogExporting(false);
      }
    };

    return (
      <>
        {AuthService.canCreateCustomer() && (
          <button className="btn btn-primary me-2" onClick={() => goToCreateNew()}>
            <Plus size={16}></Plus>Add New Customer
          </button>
        )}
        {AuthService.canEditAnyCustomerTab() && (
          <button
            className="btn btn-primary me-2"
            disabled={selectedCustomers.length < 2}
            onClick={() => {
              if (selectedCustomers.length < 2) return;
              setBulkEditCustomers(
                selectedCustomers.map((customer) => ({
                  ...customer,
                  id: `${customer?.id || customer?._id || ""}`,
                }))
              );
              setShowBulkEdit(true);
            }}
          >
            <PencilSquare size={16} className="me-1"></PencilSquare>Bulk Edit
          </button>
        )}
        {AuthService.canExportCustomerReport() && (
          <Export
            columns={exportColumns}
            data={exportData}
            filename="customers"
            actionLogPageName="Customer List"
            show={showExportDropdown}
            onToggle={onExportToggle}
            customActionsBelowPdf={[
              {
                label: "Customer Admission & Discharge Log",
                actionLogLabel: "Customer Admission & Discharge Log",
                skipActionLog: true,
                onClick: () => void exportAdmissionDischargeLog(),
                disabled: admissionLogExporting,
              },
            ]}
          />
        )}
      </>
    );
  };

  return (
    <>
      <div hidden={showBulkEdit && AuthService.canEditAnyCustomerTab()}>
        <DashboardCustomersList
          key={listRefreshKey}
          showBreadcrumb={true}
          title="All Customers"
          additionalButtons={additionalButtons}
          freezeTableHeader
          enableBulkSelect={AuthService.canEditAnyCustomerTab()}
        />
      </div>
      {showBulkEdit && AuthService.canEditAnyCustomerTab() && (
        <BulkEditCustomersWizard
          customers={bulkEditCustomers}
          onCancel={() => {
            setShowBulkEdit(false);
            setBulkEditCustomers([]);
          }}
          onComplete={() => {
            setShowBulkEdit(false);
            setBulkEditCustomers([]);
            setListRefreshKey((prev) => prev + 1);
          }}
        />
      )}
    </>
  );
};

export default CustomersList;
